import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  type ClearSummary,
  clearAccountData,
  type ImportSummary,
  importPlanEat,
  parsePlanEatExport,
} from "@/lib/import/plan-eat-import";

type PageMode = "add" | "merge" | "clear";

const MODE_OPTIONS: { value: PageMode; title: string; description: string }[] =
  [
    {
      value: "add",
      title: "Add all as new",
      description: "Every recipe in the export is created as a new recipe.",
    },
    {
      value: "merge",
      title: "Merge into existing (repair)",
      description:
        "Matches recipes by title and updates them in place: meal plans are " +
        "kept, unchanged ingredient lines keep their analysis, and changed " +
        "lines are re-analysed (healing split alternatives). Local edits " +
        "made since the export may be overwritten.",
    },
    {
      value: "clear",
      title: "Clear all data first",
      description:
        "Deletes all recipes, meal plans and shopping lists for this " +
        "account (syncs to every device), then imports everything as new.",
    },
  ];

// Temporary developer route for a one-off Plan Eat import. Navigate to /import
// while signed in, choose the export JSON, and import into the current account.
// Delete this route (and plan-eat-export.json) once the import is done.
function ImportPage() {
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<PageMode>("add");
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [cleared, setCleared] = useState<ClearSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleImport() {
    if (!file) return;
    if (
      mode === "clear" &&
      !window.confirm(
        "This will first DELETE all recipes, meal plans and shopping lists for this account (syncs to every device), then import. Continue?",
      )
    ) {
      return;
    }

    setBusy(true);
    setError(null);
    setSummary(null);
    setCleared(null);
    try {
      if (mode === "clear") {
        setCleared(await clearAccountData());
      }
      const json = JSON.parse(await file.text());
      const data = parsePlanEatExport(json);
      setSummary(await importPlanEat(data, mode === "merge" ? "merge" : "add"));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex h-svh max-w-md flex-col gap-4 overflow-y-auto p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Import Plan Eat export</h1>
        <Link to="/recipes" className="text-sm text-muted-foreground underline">
          Done
        </Link>
      </div>

      <p className="text-sm text-muted-foreground">
        Choose the Plan Eat export JSON. Recipes import into the current
        account; ingredients are parsed automatically by the server after they
        sync.
      </p>

      <label className="flex cursor-pointer flex-col items-center gap-1 rounded-md border-2 border-dashed border-input px-4 py-6 text-center transition-colors hover:border-primary hover:bg-muted">
        <span className="text-sm font-medium">
          {file ? file.name : "Choose export file"}
        </span>
        <span className="text-xs text-muted-foreground">
          {file
            ? "Tap to choose a different file"
            : "JSON exported from Plan Eat"}
        </span>
        <input
          type="file"
          accept="application/json,.json"
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            setSummary(null);
            setCleared(null);
            setError(null);
          }}
          className="sr-only"
        />
      </label>

      <fieldset className="space-y-2">
        <legend className="sr-only">Existing data</legend>
        {MODE_OPTIONS.map((option) => (
          <label
            key={option.value}
            className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 ${
              option.value === "clear" ? "border-destructive/50" : ""
            } ${mode === option.value ? "bg-muted" : ""}`}
          >
            <input
              type="radio"
              name="import-mode"
              value={option.value}
              checked={mode === option.value}
              onChange={() => setMode(option.value)}
              className="mt-0.5 accent-primary"
            />
            <span className="flex flex-col gap-1">
              <span
                className={`text-sm font-medium ${
                  option.value === "clear" ? "text-destructive" : ""
                }`}
              >
                {option.title}
              </span>
              <span className="text-sm text-muted-foreground">
                {option.description}
              </span>
            </span>
          </label>
        ))}
      </fieldset>

      {error && (
        <p className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </p>
      )}

      {cleared && (
        <div className="rounded-md border bg-card p-4 text-sm text-card-foreground">
          <p className="font-medium">Account data cleared</p>
          <ul className="mt-2 space-y-1 text-muted-foreground">
            <li>Recipes deleted: {cleared.recipes}</li>
            <li>Meal plan entries deleted: {cleared.mealPlanEntries}</li>
            <li>Shopping items deleted: {cleared.shoppingItems}</li>
            <li>Removed items deleted: {cleared.removedItems}</li>
          </ul>
        </div>
      )}

      {summary && (
        <div className="rounded-md border bg-card p-4 text-sm text-card-foreground">
          <p className="font-medium">Import complete</p>
          <ul className="mt-2 space-y-1 text-muted-foreground">
            <li>Recipes created: {summary.created}</li>
            <li>Recipes updated: {summary.updated}</li>
            {summary.updated > 0 && (
              <>
                <li>Ingredient lines kept: {summary.ingredientsReused}</li>
                <li>
                  Ingredient lines re-analysing: {summary.ingredientsReanalysed}
                </li>
              </>
            )}
            {summary.matchedDuplicateTitles > 0 && (
              <li>
                Same-title local duplicates not merged:{" "}
                {summary.matchedDuplicateTitles}
              </li>
            )}
            <li>Deleted recipes skipped: {summary.skippedDeleted}</li>
            <li>Duplicate stubs dropped: {summary.duplicatesDropped}</li>
            <li>Sub-recipe links resolved: {summary.subRecipeLinks}</li>
            <li>Methods → source: {summary.methodsAsSource}</li>
            <li>Methods → URL: {summary.methodsAsUrl}</li>
            <li>Methods → instructions: {summary.methodsAsInstructions}</li>
          </ul>
        </div>
      )}

      <Button
        size="lg"
        disabled={!file || busy}
        onClick={handleImport}
        className="w-full"
      >
        {busy
          ? "Working…"
          : mode === "clear"
            ? "Clear & import"
            : mode === "merge"
              ? "Merge import"
              : "Import"}
      </Button>
    </div>
  );
}

export const Route = createFileRoute("/_app/import")({
  component: ImportPage,
});
